import type { EComDataType, ModifyCartItemByDecoratedProduct } from '../model';
import { getAnalyticsCategoryTexts } from '~/stores/cartStore/helper/getAnalyticsCategoryTexts';
import { isDefined } from '~/utils/guards/isDefined';
import { enrichEcommerceWithActionBarAttribution } from '~/utils/actionBarCrossSellAnalytics/enrichEcommerceWithActionBarAttribution';
import { getActionBarCrossSellAttributionForSku } from '~/utils/actionBarCrossSellAnalytics/getActionBarCrossSellAttributionForSku';
import { enrichEcommerceWithPdpDropdownAttribution } from '~/utils/pdpRecommendedProductsAnalytics/enrichEcommerceWithPdpDropdownAttribution';
import { getPdpRecommendedProductsAttributionForSku } from '~/utils/pdpRecommendedProductsAnalytics/getPdpRecommendedProductsAttributionForSku';
import { enrichEcommerceWithFinderAttribution } from '~/utils/productFinderAnalytics/enrichEcommerceWithFinderAttribution';
import { getProductFinderAttributionForSku } from '~/utils/productFinderAnalytics/getProductFinderAttributionForSku';
import type {
  OrderAddress,
  ShippingCartAddressFragment
} from '~~/lib/Shop/generated/schema';
import type { OptionalMoneyFragment } from '~/utils/shop/formatMoney';
/**
 * Creates a single entry inside an analytics event.
 *
 * @see https://gcp.baslerweb.com/jira/browse/WEB2-1695
 *
 * @param decoratedProduct The decorated product to use.
 * @param quantity The quantity to use.
 * @param resolvedBreadcrumbs Optional pre-resolved breadcrumb trail.
 */
function transformItem({
  decoratedProduct,
  quantity,
  breadcrumbs: resolvedBreadcrumbs
}: ModifyCartItemByDecoratedProduct) {
  const categoryTexts = getAnalyticsCategoryTexts(
    decoratedProduct,
    resolvedBreadcrumbs
  );
  const finderAttribution = decoratedProduct.sku
    ? getProductFinderAttributionForSku(decoratedProduct.sku)
    : undefined;
  const actionBarAttribution = decoratedProduct.sku
    ? getActionBarCrossSellAttributionForSku(decoratedProduct.sku)
    : undefined;
  const pdpDropdownAttribution = decoratedProduct.sku
    ? getPdpRecommendedProductsAttributionForSku(decoratedProduct.sku)
    : undefined;

  return {
    item_id: decoratedProduct.sku, // sku_107822
    item_name: decoratedProduct.modelName, // a2A1920-51gmBAS - Basler ace 2
    item_category: categoryTexts[0] ?? decoratedProduct.category ?? '',
    item_category_id: decoratedProduct.categoryId, // Area scan camera
    item_category2: categoryTexts[1] ?? '',
    item_category3: categoryTexts[2] ?? '',
    item_category4: categoryTexts[3] ?? '',
    item_category5: categoryTexts[4] ?? '',
    item_brand: decoratedProduct.brandData?.[0]?.label?.label ?? '',
    price: decoratedProduct.price, // 15.5
    quantity, // 1 -- always 1 in case of view_item
    delivery_time:
      decoratedProduct.showShippingTime &&
      isDefined(decoratedProduct.shippingTimeText)
        ? decoratedProduct.shippingTimeText // 4-5 weeks
        : 'Not available',
    fastdelivery_available: decoratedProduct.hasFastDelivery,
    product_type: decoratedProduct.getAttributeSetName(),
    discount: decoratedProduct.discount,
    ...(finderAttribution
      ? {
          item_list_id: finderAttribution.item_list_id,
          item_list_name: finderAttribution.item_list_name,
          selected_via_finder: finderAttribution.selected_via_finder,
          finder: finderAttribution.finder,
          finder_entry: finderAttribution.finder_entry,
          finder_session_id: finderAttribution.finder_session_id,
          ...(finderAttribution.finder_camera_sku
            ? { finder_camera_sku: finderAttribution.finder_camera_sku }
            : {})
        }
      : {}),
    ...(actionBarAttribution
      ? {
          item_list_id: actionBarAttribution.item_list_id,
          item_list_name: actionBarAttribution.item_list_name,
          selected_via_action_bar: actionBarAttribution.selected_via_action_bar,
          ab_session_id: actionBarAttribution.ab_session_id,
          ab_category: actionBarAttribution.ab_category,
          ab_result_position: actionBarAttribution.ab_result_position,
          ab_main_product_sku: actionBarAttribution.ab_main_product_sku
        }
      : {}),
    ...(pdpDropdownAttribution
      ? {
          item_list_id: pdpDropdownAttribution.item_list_id,
          item_list_name: pdpDropdownAttribution.item_list_name,
          selected_via_pdp_dropdown:
            pdpDropdownAttribution.selected_via_pdp_dropdown,
          pdp_category: pdpDropdownAttribution.pdp_category,
          pdp_result_position: pdpDropdownAttribution.pdp_result_position,
          pdp_main_product_sku: pdpDropdownAttribution.pdp_main_product_sku
        }
      : {})
  };
}

export async function createEcommerceTrackingData({
  cartItems,
  source = 'website',
  tax,
  transactionId,
  coupon,
  shipping,
  paymentType,
  checkoutStep,
  cartId
}: {
  cartItems: ReadonlyArray<ModifyCartItemByDecoratedProduct>;
  source?: string;
  tax?: OptionalMoneyFragment;
  transactionId?: string;
  coupon?: string;
  shipping?: ShippingCartAddressFragment | OrderAddress;
  paymentType?: string;
  checkoutStep?: string;
  cartId?: string;
}) {
  const [firstItem] = cartItems;
  if (!firstItem) {
    return;
  }

  const eComData: EComDataType = {
    currency: firstItem.decoratedProduct.productCurrency, // $
    value: cartItems.reduce((collector, { decoratedProduct, quantity }) => {
      const price = Number(decoratedProduct.price);
      if (quantity <= 0 || isNaN(price)) {
        return collector;
      }

      return collector + quantity * price;
    }, 0), // 15.5
    items: cartItems.map(transformItem),
    ecom_source: source // @see https://gcp.baslerweb.com/jira/browse/WEB2-1724
  };

  if (tax) {
    eComData.tax = tax;
  }

  if (transactionId) {
    eComData.transaction_id = transactionId;
  }

  if (checkoutStep) {
    eComData.checkout_step = checkoutStep;
  }

  if (coupon) {
    eComData.coupon = coupon;
  }

  if (shipping) {
    eComData.shipping = shipping;
  }

  if (paymentType) {
    eComData.payment_type = paymentType;
  }

  if (cartId) {
    eComData.cart_id = cartId;
  }

  return enrichEcommerceWithPdpDropdownAttribution(
    enrichEcommerceWithActionBarAttribution(
      enrichEcommerceWithFinderAttribution(eComData, source),
      source
    )
  );
}
